/* eslint-disable react-hooks/exhaustive-deps */
/**
 * MiniCartProvider
 * Contain most logic mini cart
 */
import _ from 'lodash'
import URI from 'urijs'
import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  useCurrencies,
  useDisableCheckouts,
  useOrders,
  useStores,
  // useSystemSettings,
} from '../../vendor/react-omnitech-api'
import env from '../../config/env'
import getCartPrices from '../../helpers/get-cart-prices'
import validateCartMax from '../../helpers/validate-cart-max'
import groupCartLineProperties from '../../helpers/group-cart-line-properties'
import cancelRequest from '../../helpers/cancel-request'
import moment from '../../helpers/moment'
import { useAlert } from '../use-alert'
import { useCart } from '../use-cart'
import { useLink } from '../use-link'
import { useModal } from '../use-modal'
import useSku from '../use-sku'
import useOrderMethod from '../use-order-method'
import MiniCartContext from './mini-cart-context'
import useAnalytics from '../use-analytics'

export default function MiniCartProvider({ children }) {
  // internal state
  const [
    selectedCartCommerceChannel,
    setSelectedCartCommerceChannel,
  ] = useState()
  const [isActive, setIsActive] = useState(false)
  const [cart, setCart] = useState({})
  const [disableCheckouts, setDisableCheckouts] = useState([])
  const [storeMenuCodes, setStoreMenuCodes] = useState([])
  const [loadings, setLoadings] = useState([])
  const [miniCartOpen, setMiniCartOpen] = useState(false)
  const [skusUnavailableToCheckout, setSkusUnavailableToCheckout] = useState([])

  const skuQuotaCommerceChannel = _.get(env, 'config.api.commerceChannelForSkuQuota')

  // prepare hook
  const alert = useAlert()
  const { currencies } = useCurrencies()
  const { navigate } = useLink()
  const modal = useModal()
  const { t } = useTranslation()
  const { trackEvent } = useAnalytics()
  const {
    updateOrderToDecline,
  } = useOrders()
  const {
    refreshCartsInProgress,
    carts,
    cartIds,
    findOrCreateCart,
    createIsolateCart,
    cartsTotalItemsCount,
    // totalItemsCount: totalItems,
    updateCart,
    getCartLineAvailableQuantity,
    getCartTotalItemsCount,
    getCartLineErrors,
    getCartValidationErrors,
  } = useCart()
  const {
    fetchDisableCheckoutsAll,
  } = useDisableCheckouts()
  const {
    fetchStoreDateTimeInformation,
  } = useStores()
  const {
    availableOrderMethod,
    orderMethod,
  } = useOrderMethod()
  const orderMethodMandatory = _.includes(
    _.map(availableOrderMethod, 'commerceChannel'),
    selectedCartCommerceChannel,
  )
  const { isAllowToCheckout } = useSku({
    cart,
    commerceChannel: selectedCartCommerceChannel,
    orderMethodMandatory,
  })
  // const { getSystemSetting } = useSystemSettings()
  // const fnbEnabled = getSystemSetting('features.fnb.enable')

  const orderMethodCommerceChannel = _.get(orderMethod, 'commerceChannel')
  const availableCarts = _.compact([
    skuQuotaCommerceChannel && {
      key: 'skuQuota',
      type: 'skuQuota',
      cartId: _.get(cartIds, skuQuotaCommerceChannel),
      commerceChannel: skuQuotaCommerceChannel,
      totalItemsCount: _.get(cartsTotalItemsCount, skuQuotaCommerceChannel, 0),
      active: selectedCartCommerceChannel === skuQuotaCommerceChannel,
    },
    {
      key: 'orderMethod',
      type: 'orderMethod',
      cartId: _.get(cartIds, orderMethodCommerceChannel),
      commerceChannel: orderMethodCommerceChannel,
      totalItemsCount: _.get(cartsTotalItemsCount, orderMethodCommerceChannel, 0),
      orderMethod,
      active: selectedCartCommerceChannel === orderMethodCommerceChannel,
    },
    // [TODO] support show all order methods cart
  ])

  const totalItems = _.sumBy(availableCarts, 'totalItemsCount')

  const getExtraParams = (commerceChannel) => {
    const includeGroups = ['basic', 'cart']
    if (
      skuQuotaCommerceChannel
      && commerceChannel === skuQuotaCommerceChannel
    ) {
      includeGroups.push('skuQuota')
      return {
        type: 'skuQuota',
        includeGroups,
      }
    }
    return {
      includeGroups,
    }
  }

  /**
   * apiFetchCart
   * get cart data from API
   */
  // function apiFetchCart() {
  //   const options = {
  //     includeGroups: ['miniCart'],
  //   }
  //   return findOrCreateCart(options)
  // }
  const cartId = useMemo(() => (
    _.get(cartIds, selectedCartCommerceChannel)
  ), [selectedCartCommerceChannel, cartIds])
  const apiFetchCart = useCallback(() => {
    const extraParams = getExtraParams(selectedCartCommerceChannel)
    return findOrCreateCart({
      // orderMethodMandatory: false,
      cartId,
      commerceChannel: selectedCartCommerceChannel,
      ...extraParams,
      cancelKey: 'fetchCart|useMiniCart',
    // }).catch((errors) => {
    //   if (/^(20009)$/.test(_.get(errors, 'generalError.code'))) {
    //     setCartRequireUser(true)
    //   } else {
    //     throw errors
    //   }
    })
  }, [cartId])

  const checkoutDisabled = useMemo(() => (
    !_.isEmpty(
      _.find(
        disableCheckouts,
        {
          commerceChannel: selectedCartCommerceChannel,
          deliveryType: _.get(cart, 'cartShipments.0.deliveryType.code'),
        },
      ),
    )
  ), [disableCheckouts, selectedCartCommerceChannel, cart])

  /**
   * apiUpdateCart
   * call API to update cart
   * @param {*} actions
   */
  function apiUpdateCart(actions) {
    // const cartId = _.get(cartIds, selectedCartCommerceChannel)
    const extraParams = getExtraParams(selectedCartCommerceChannel)
    const option = {
      cartId,
      commerceChannel: selectedCartCommerceChannel,
      cancelKey: 'updateCart|useMiniCart',
      payload: {
        data: {
          actions,
        },
        batchUpdateMode: 2,
      },
      params: extraParams,
    }
    return updateCart(option)
  }

  /**
   * handleError
   * @param {*} error
   */
  async function handleError(error) {
    const generalError = _.get(error, 'generalError', {})
    let errorMessage = ''
    if (generalError.code === 404) {
      errorMessage = t('screens.cart.errors.cartNotFound')
    } else {
      const batchActionErrors = _.get(error, 'batchActionErrors', [])
      const batchActionError = _.find(batchActionErrors, 'message') || {}
      // if batch action is not provided, use the general error message
      errorMessage = _.isEmpty(batchActionError.message)
        ? generalError.message
        : batchActionError.message
    }
    alert.show(errorMessage, { state: 'error' })
  }

  /**
   * function handleFetchCart() {
   */
  async function handleFetchCart() {
    cancelRequest.cancel([
      'fetchCart|useMiniCart',
    ]);
    setLoadings((prevLoadingList) => _.uniq([...prevLoadingList, 'fetchCart']))
    try {
      // call api
      const { cart: latestCart } = await apiFetchCart()
      // get store dateTimeInformation before valdation
      const storeId = _.get(latestCart, 'cartShipments.0.inventoryStore.id')
      const [
        { dateTimeInformation },
        { disableCheckouts: disableCheckoutsData },
      ] = await Promise.all([
        fetchStoreDateTimeInformation({
          id: storeId,
          date: moment().format('YYYY-MM-DD'),
        }),
        fetchDisableCheckoutsAll({
          storeId,
          commerceChannelCodeEq: selectedCartCommerceChannel,
        }),
      ])
      setCart(latestCart)
      setStoreMenuCodes(_.get(dateTimeInformation, 'menuCodes', []))
      setDisableCheckouts(disableCheckoutsData)
      return latestCart
    } catch (error) {
      await handleError(error)
    } finally {
      setLoadings((prevLoadingList) => _.without(prevLoadingList, 'fetchCart'))
    }
  }

  function onTrackRemoveFromCart(cartLineId) {
    const { cartLineProperties } = cart
    const cartLineProperty = _.find(cartLineProperties, {
      id: cartLineId,
    })
    const name = _.get(cartLineProperty, 'sku.product.title')
    const skuCode = _.get(cartLineProperty, 'sku.code')
    const price = _.get(cartLineProperty, 'priceDetails.propertyUnitPrice')
    const quantity = _.get(cartLineProperty, 'quantity')
    const title = 'Page Mini-Cart'

    trackEvent('customerRemoveFromCart',
      {},
      {
        name,
        price,
        quantity,
        skuCode,
        title,
      })
  }

  /**
   * function handleCloseMiniCart() {
   * close mini cart
   */
  function handleCloseMiniCart() {
    setMiniCartOpen(false)
  }

  /**
   * function handleOpenMiniCart() {
   * open mini cart
   */
  function handleOpenMiniCart(options = {}) {
    let cartMatches = _.pick(options, ['cartId', 'commerceChannel', 'type', 'active'])
    cartMatches = {
      ..._.omit(cartMatches, ['type']),
      key: _.get(cartMatches, 'type'),
    }
    const selectedCart = _.find(availableCarts, cartMatches)
    if (selectedCart) {
      onCartChange(selectedCart.commerceChannel)
    }
    setMiniCartOpen(true)
  }

  /**
   * handleUpdateCartLineQuantity
   * call API to update cart line quantity
   */
  function handleUpdateCartLineQuantity({
    skuId,
    quantity,
    groupUuid,
    identifierUuid,
    skuQuotaId,
    skuQuotaProfileId,
    skuQuotaWaitingList,
  }) {
    const actions = [
      {
        actionType: 'update_cart_line_property',
        quantityMode: 'fixed',
        skuId,
        quantity,
        productAddonId: null,
        groupUuid,
        identifierUuid,
        parentIdentifierUuid: null,
        ..._.omitBy(
          {
            skuQuotaId,
            skuQuotaProfileId,
            skuQuotaWaitingList,
          },
          _.isUndefined,
        ),
      },
    ]
    handleUpdateCart(actions)
  }

  /**
   * handleRemoveCartLine
   */
  function handleRemoveCartLine(item) {
    handleUpdateCartLineQuantity({
      ...item,
      quantity: 0,
    })
    onTrackRemoveFromCart(item.id)
  }

  /**
   * handleUpdateCartShipment
   */
  function handleUpdateCartShipment({ shipmentId, ...data }) {
    // data: {deliveryType, deliveryAddressId, pickupStoreId}
    const updateCartAction = _.omitBy(
      data,
      (value) => (
        _.isNil(value)
        || value === '' // prevent empty string
      ),
    )
    const actions = [{
      actionType: 'update_cart_shipment',
      id: shipmentId,
      ...updateCartAction,
    }]
    handleUpdateCart(actions)
  }

  /**
   * handleUpdateCart
   * handle all update cart actions
   * @param {*} actions
   */
  async function handleUpdateCart(actions) {
    alert.remove()
    setLoadings((prevLoadingList) => _.uniq([...prevLoadingList, 'updateCart']))
    try {
      const { cart: data } = await apiUpdateCart(actions)
      setCart(data)
    } catch (error) {
      await handleError(error)
    } finally {
      setLoadings((prevLoadingList) => _.without(prevLoadingList, 'updateCart'))
    }
  }

  function checkSkuAllowToCheckout(skus) {
    // pass-in cart exclusions
    const exclusions = _.get(orderMethod, 'cartSkuCheckExclusions', {})
    setSkusUnavailableToCheckout(
      _.reject(skus, (s) => isAllowToCheckout({ sku: s, storeMenuCodes, exclusions })),
    )
  }

  const onCartChange = (cartCommerceChannelCode) => {
    // cancelRequest.cancel([
    //   'fetchCart|useMiniCart',
    // ])
    setSelectedCartCommerceChannel(cartCommerceChannelCode)
  }

  const cartUpdatedAt = useMemo(() => _.get(cart, 'cartUpdatedAt'), [cart])
  const cartsTotalItemsCountReady = useMemo(() => (
    !(refreshCartsInProgress || _.isEmpty(carts))
  ), [refreshCartsInProgress, carts])
  /**
   * Auto close mini cart when every carts are empty
   */
  useEffect(() => {
    if (
      cartsTotalItemsCountReady
      && _.sumBy(availableCarts, 'totalItemsCount') === 0
    ) {
      handleCloseMiniCart()
    }
  }, [cartsTotalItemsCountReady])

  useEffect(() => {
    if ((miniCartOpen || isActive) && cartsTotalItemsCountReady) {
      const cartWithItems = _.find(availableCarts, ({ totalItemsCount }) => totalItemsCount > 0)
      const cartCommerceChannelWithItems = _.get(cartWithItems, 'commerceChannel')
      // set default selected cart commerce channel
      let defaultSelectedCartCommerceChannel = selectedCartCommerceChannel
      if (_.isEmpty(defaultSelectedCartCommerceChannel)) {
        // find the cart with items if no cart selected
        defaultSelectedCartCommerceChannel = cartCommerceChannelWithItems
      }
      if (_.isEmpty(defaultSelectedCartCommerceChannel)) {
        // use order method commerce channel if no cart have items
        defaultSelectedCartCommerceChannel = orderMethodCommerceChannel
      }
      if (
        selectedCartCommerceChannel
        && cartCommerceChannelWithItems
        && _.get(cartsTotalItemsCount, selectedCartCommerceChannel || '--not-found--', 0) === 0
      ) {
        // Auto switch cart when selected cart become empty
        defaultSelectedCartCommerceChannel = cartCommerceChannelWithItems
      }
      const onCartChangeTimer = _.delay(() => {
        // delay 50ms to ensure cart & loadings states are updated
        onCartChange(defaultSelectedCartCommerceChannel)
      }, 50)

      return function cleanup() {
        clearTimeout(onCartChangeTimer)
      }
    }
  }, [
    selectedCartCommerceChannel,
    miniCartOpen,
    isActive,
    cartUpdatedAt,
    cartsTotalItemsCount,
    cartsTotalItemsCountReady,
  ])

  /**
   * check once mini cart is open, fetch cart data
   */
  useEffect(() => {
    if (
      (miniCartOpen || isActive)
      && cartsTotalItemsCountReady
      && selectedCartCommerceChannel
      && _.isEmpty(loadings)
    ) {
      handleFetchCart()
    }
    return function cleanup() {
      cancelRequest.cancel([
        'fetchCart|useMiniCart',
        'updateCart|useMiniCart',
      ]);
    }
  }, [selectedCartCommerceChannel, miniCartOpen, isActive, cartsTotalItemsCountReady])

  /**
   * check skus are allow to checkout for fnb
   */
  useEffect(() => {
    const skus = _.uniqBy(_.map(_.get(cart, 'cartLineProperties', []), 'sku'), 'id')
    if (!_.isEmpty(skus) && !_.isEmpty(availableOrderMethod)) {
      checkSkuAllowToCheckout(skus)
    }
  }, [cart, availableOrderMethod])

  // const {
  //   cartLineProperties = [],
  //   currencyCode,
  //   cartMaxNumOfQtyPerSku,
  //   cartMaxNumOfQty,
  // } = cart

  const cartLineItems = useMemo(() => {
    const {
      cartLineProperties = [],
      cartMaxNumOfQtyPerSku,
      cartMaxNumOfQty,
    } = cart
    const groupedCartLineProperties = groupCartLineProperties(cartLineProperties)
    return (
      _.map(groupedCartLineProperties, (cartLine) => {
        // const showNotAllowedToCheckoutMessage = !_.isEmpty(
        //   _.intersection(
        //     [
        //       _.get(item, 'skuId'),
        //       ..._.map(_.get(item, 'addons', []), 'skuId'),
        //     ],
        //     _.map(skusUnavailableToCheckout, 'id'),
        //   ),
        // )
        const maxSelectableQty = getCartLineAvailableQuantity({ cart, cartLine })
        const cartMaxAvailableNumOfQty = _.max([cartMaxNumOfQty - maxSelectableQty, 0])
        const errors = getCartLineErrors({
          cart,
          cartLine,
          storeMenuCodes,
          exclusions: orderMethodMandatory
            ? _.get(orderMethod, 'cartSkuCheckExclusions', {})
            : {
              commerceChannel: true,
              store: true,
              stockLevel: true,
              timeSlot: true,
              storeOpeningHours: true,
              dayOfWeek: true,
            },
        })
        return {
          // ...showNotAllowedToCheckoutMessage && {
          //   inlineError: t('ui.dineInMiniCart.itemUnavailable'),
          // },
          cartLine,
          errors,
          maxSelectableQty,
          cartMaxNumOfQtyPerSku,
          cartMaxNumOfQty,
          cartMaxAvailableNumOfQty,
        }
      })
    )
  }, [cartUpdatedAt])

  const cartCurrencyCode = useMemo(() => (
    _.get(cart, 'overrideCurrencyCode')
    || _.get(cart, 'currencyCode')
  ), [cart])
  const cartCurrency = useMemo(() => (
    _.find(currencies, { code: cartCurrencyCode })
  ), [cartCurrencyCode])

  const cartPriceSummary = useMemo(() => ({
    ...getCartPrices(cart),
    quantityCount: getCartTotalItemsCount(cart),
  }), [cart])

  const cartErrors = useMemo(() => {
    const cartMaxErrors = validateCartMax(cart)
    const errors = _.uniq(_.compact([
      ...(_.isArray(cartMaxErrors) ? cartMaxErrors : []),
      ...getCartValidationErrors(
        _.get(cart, 'validationErrors', {}),
        { type: 'cart' },
      ),
    ]))
    return _.map(errors, (code) => (
      t(
        'screens.cart.errors.cart',
        {
          context: code,
        },
      )
    ))
  }, [cart])

  const handleCheckout = async () => {
    try {
      const { cart: newCart } = await apiFetchCart()
      // decline previous order if it is exist
      const awaitConfirmOrder = _.get(newCart, 'awaitConfirmOrder')
      if (
        _.has(awaitConfirmOrder, 'uuid')
      ) {
        if (
          _.get(awaitConfirmOrder, 'source', null) === 'ecom'
          && _.get(awaitConfirmOrder, 'isCheckoutOperator', false)
        ) {
          modal.open({
            key: 'declineOrderConfirmation',
            onContinue: () => {
              // close modal
              modal.close()
              // decline order
              updateOrderToDecline({
                commerceChannel: selectedCartCommerceChannel,
                byType: 'by_uuid',
                uuid: _.get(awaitConfirmOrder, 'uuid'),
              })
                .then(() => {
                  // retry checkout
                  handleCheckout()
                })
            },
            onCancel: () => {
              // close modal
              modal.close()
            },
          })
        } else {
          modal.open({
            key: 'checkoutInProgress',
            onCancel: () => {
              // close modal
              modal.close()
            },
          })
        }
      } else {
        let isolateCartId = _.get(newCart, 'id')
        if (_.get(newCart, 'state') !== 'isolated') {
          const option = {
            cartId: isolateCartId,
            commerceChannel: selectedCartCommerceChannel,
            // params: {
            // },
            checksum: newCart.checksum,
          }
          const { cart: isolateCart } = await createIsolateCart(option)
          isolateCartId = _.get(isolateCart, 'id')
        }
        const checkoutUrl = URI('/checkout/')
          .search({
            id: isolateCartId,
            originalId: _.get(newCart, 'id'),
          })
        navigate(
          `${checkoutUrl.path()}${checkoutUrl.search()}`,
          {
            commerceChannel: selectedCartCommerceChannel,
          },
        )
      }
    } catch (error) {
      handleError(error)
    }
  }

  const state = {
    availableCarts,
    cart,
    cartCurrency,
    cartErrors,
    cartLineItems,
    cartPriceSummary,
    checkoutDisabled,
    loadings,
    miniCartOpen,
    totalItems,
    skusUnavailableToCheckout,
    orderMethodMandatory,
    setIsActive,
    openMiniCart: handleOpenMiniCart,
    closeMiniCart: handleCloseMiniCart,
    onCartChange,
    onAddCartLineToWaitlist: handleUpdateCartLineQuantity,
    onUpdateCartShipment: handleUpdateCartShipment,
    onUpdateCartLineQuantity: handleUpdateCartLineQuantity,
    onRemoveCartLine: handleRemoveCartLine,
    onRequestRefreshCart: handleFetchCart,
    onRequestCheckout: handleCheckout,
    onSelectedCommerceChannelChange: setSelectedCartCommerceChannel,
  }

  return (
    <MiniCartContext.Provider value={state}>
      {children}
    </MiniCartContext.Provider>
  )
}
